import { isDefined } from '@avantia/form-validation';
import { formatCurrency, formatNumber } from '../../../tools/formatting';
import {
  HpClientQuestionData,
  HpClientStandardInputTextAndStyle,
  HpControlValueTypes,
  HpEventTypes,
  OptionalQuestionUI
} from '../../../types';
import { HpTextInputControlFormatter, HpTextInputControlProps, TextInputControl } from './textInputControl';

export type HpCurrencyInputControlProps = HpTextInputControlProps;

/**
 * A Currency Input Control - fractional currency values will be clipped.
 */
export function CurrencyInputControl(props: HpCurrencyInputControlProps): JSX.Element {
  const formatter: HpTextInputControlFormatter = {
    do: (val: HpControlValueTypes): string => {
      const num = parseInt(val as string);
      return formatNumber(num);
    },
    undo: (input, ui, eventType) => formatterUndo(input, ui, eventType, false)
  };
  return currencyInputControl(props, formatter);
}

/**
 * A Currency Input Control - fractional currency values will be kept.
 */
export function CurrencyFractionInputControl(props: HpCurrencyInputControlProps): JSX.Element {
  const formatter: HpTextInputControlFormatter = {
    do: (val: HpControlValueTypes, ui: OptionalQuestionUI): string => {
      const v = val as string;
      return ui && !ui.hasFocus ? formatCurrencyIfNumeric(v) : v;
    },
    undo: (input, ui, eventType) => {
      return formatterUndo(input, ui, eventType, true);
    }
  };
  return currencyInputControl(props, formatter);
}

function formatterUndo(
  input: HpControlValueTypes,
  ui: OptionalQuestionUI,
  eventType: HpEventTypes,
  allowFraction: boolean
): string {
  const val = input as string;
  if (allowFraction && eventType !== 'blur') {
    return val;
  }

  let res = '';
  const len = val.length;
  for (let i = 0; i < len; i++) {
    const c = val[i];
    if (c >= '0' && c <= '9') {
      res += c;
    } else if (allowFraction && c === '.') {
      res += c;
    }
  }

  if (eventType === 'blur' && allowFraction) {
    res = formatCurrencyIfNumeric(res);
  }

  return res;
}

function formatCurrencyIfNumeric(res: string): string {
  if (res) {
    const num = parseFloat(res);
    if (!isNaN(num)) {
      res = formatCurrency(num, { includePence: true, includeSymbol: false });
    }
  }

  return res;
}

function currencyInputControl(props: HpCurrencyInputControlProps, formatter: HpTextInputControlFormatter): JSX.Element {
  const { data, formatType } = props;
  const { textAndStyle } = data as HpClientQuestionData;
  const { prefixText, suffixText } = textAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const currProps = { ...props };
  currProps.prefixText = !isDefined(prefixText) ? '£' : prefixText;
  currProps.suffixText = suffixText;
  currProps.formatter = formatter;
  return TextInputControl(currProps);
}
